import { computeQuoteTotalRangeFromBlocks, formatAmountRange } from '../../../backend/src/utils/quoteTotal';
import { useCallback, useMemo } from 'react';
import type { Block, HubSpotCompany, HubSpotContact, HubSpotDeal, HubSpotVariable, InvestmentBlockContent, Quote } from '@/types';
import { fillMissingHubSpotSalesPlaceholders } from '@/lib/hubspotVariablePlaceholders';
import type { HubSpotQuoteData } from './useHubSpotQuoteData';

const toLabel = (rawKey: string) => {
  const spaced = rawKey
    .replace(/_/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

const toValue = (value: unknown) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const formatCurrency = (raw: string | number | undefined): string => {
  if (raw === undefined || raw === null) return '';
  const num = typeof raw === 'string' ? parseFloat(raw.replace(',', '.')) : raw;
  if (Number.isNaN(num)) return String(raw);
  return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(num);
};

const formatHubSpotDate = (raw: string | number | undefined): string => {
  if (raw === undefined || raw === null) return '';
  const str = String(raw);
  const epochMs = /^\d{10,}$/.test(str) ? parseInt(str, 10) : NaN;
  const date = epochMs ? new Date(epochMs > 1e12 ? epochMs : epochMs * 1000) : new Date(str);
  if (Number.isNaN(date.getTime())) return str;
  return date.toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const buildVariablesFromProperties = (
  prefix: string,
  labelPrefix: string,
  properties?: Record<string, unknown>,
): HubSpotVariable[] => {
  if (!properties) return [];
  return Object.entries(properties)
    .filter(([key]) => key && key !== 'id')
    .map(([key, value]) => ({
      tag: `${prefix}.${key}`,
      label: `${labelPrefix} ${toLabel(key)}`,
      value: toValue(value),
    }));
};

interface UseQuoteVariablesParams {
  hubspotData: HubSpotQuoteData;
  primaryContact: HubSpotContact | undefined;
  localQuote: Quote;
  blocks: Block[];
  hubspotDataLoaded: boolean;
}

/**
 * Bouwt de lijst HubSpot-variabelen + token→waarde map en de resolver.
 * Let op: de quote-total berekening spiegelt logica in packages/backend/src/routes/public.ts — niet wijzigen.
 */
export function useQuoteVariables({
  hubspotData,
  primaryContact,
  localQuote,
  blocks,
  hubspotDataLoaded,
}: UseQuoteVariablesParams) {
  const variables = useMemo<HubSpotVariable[]>(() => {
    const deal = (hubspotData.deal?.properties || {}) as HubSpotDeal['properties'];
    const company = (hubspotData.company?.properties || {}) as HubSpotCompany['properties'];
    const contact = (primaryContact?.properties || {}) as HubSpotContact['properties'];
    const contactFullName = [contact.firstname, contact.lastname].filter(Boolean).join(' ');

    const dealName = deal.dealname || '';
    const dealAmount = formatCurrency(deal.amount);
    const dealCloseDate = formatHubSpotDate(deal.closedate);

    const baseVariables: HubSpotVariable[] = [
      { tag: 'deal.name', label: 'Dealnaam', value: dealName },
      { tag: 'deal.dealname', label: 'Dealnaam (HubSpot)', value: dealName },
      { tag: 'deal.amount', label: 'Dealbedrag', value: dealAmount },
      { tag: 'deal.stage', label: 'Deal status', value: deal.dealstage || '' },
      { tag: 'deal.dealstage', label: 'Deal status (HubSpot)', value: deal.dealstage || '' },
      { tag: 'deal.close_date', label: 'Sluitingsdatum', value: dealCloseDate },
      { tag: 'deal.closedate', label: 'Sluitingsdatum (HubSpot)', value: dealCloseDate },
      { tag: 'company.name', label: 'Bedrijfsnaam', value: company.name || '' },
      { tag: 'company.address', label: 'Adres', value: company.address || '' },
      { tag: 'company.zip', label: 'Postcode', value: company.zip || '' },
      { tag: 'company.city', label: 'Plaats', value: company.city || '' },
      { tag: 'company.country', label: 'Land', value: company.country || '' },
      { tag: 'company.domain', label: 'Domein', value: company.domain || '' },
      { tag: 'company.website', label: 'Website', value: company.website || company.domain || '' },
      { tag: 'company.industry', label: 'Branche', value: company.industry || '' },
      { tag: 'company.phone', label: 'Telefoon', value: company.phone || '' },
      { tag: 'contact.firstname', label: 'Voornaam', value: contact.firstname || '' },
      { tag: 'contact.lastname', label: 'Achternaam', value: contact.lastname || '' },
      { tag: 'contact.fullname', label: 'Volledige naam', value: contactFullName || '' },
      { tag: 'contact.name', label: 'Contactnaam', value: contactFullName || '' },
      { tag: 'contact.email', label: 'E-mail', value: contact.email || '' },
      { tag: 'contact.phone', label: 'Telefoon', value: contact.phone || '' },
      { tag: 'contact.mobilephone', label: 'Mobiel', value: contact.mobilephone || '' },
      { tag: 'contact.jobtitle', label: 'Functie', value: contact.jobtitle || '' },
    ];

    const baseTags = new Set(baseVariables.map((variable) => variable.tag));
    const extraDeal = buildVariablesFromProperties('deal', 'Deal', deal).filter((v) => !baseTags.has(v.tag));
    const extraCompany = buildVariablesFromProperties('company', 'Bedrijf', company).filter((v) => !baseTags.has(v.tag));
    const extraContact = buildVariablesFromProperties('contact', 'Contact', contact).filter((v) => !baseTags.has(v.tag));

    const extras = [...extraDeal, ...extraCompany, ...extraContact].sort((a, b) => a.tag.localeCompare(b.tag));
    return [...baseVariables, ...extras];
  }, [hubspotData.deal, hubspotData.company, primaryContact]);

  const variablesMap = useMemo(() => {
    const map = variables.reduce<Record<string, string>>((acc, variable) => {
      acc[`{{${variable.tag}}}`] = variable.value || '';
      return acc;
    }, {});
    const now = new Date();
    const todayStr = now.toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' });
    map['{{date.today}}'] = todayStr;
    map['{{date.year}}'] = String(now.getFullYear());
    map['{{quote.date}}'] = todayStr;
    map['{{quote.valid_until}}'] = localQuote.expiresAt
      ? new Date(localQuote.expiresAt).toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' })
      : todayStr;
    map['{{quote.number}}'] = localQuote.quoteNumber || localQuote.id?.slice(0, 8) || localQuote.slug || '';
    map['{{contact.name}}'] = [primaryContact?.properties?.firstname, primaryContact?.properties?.lastname].filter(Boolean).join(' ') || map['{{contact.fullname}}'] || '';
    const hasProducts = blocks.some((block) => (block.content as InvestmentBlockContent)?.tables
      ?.some((table) => table.rows.some((row) => row.investmentProduct || row.productPrice)));
    // Existing quote variables included optional rows; preserve that legacy output.
    const total = computeQuoteTotalRangeFromBlocks(blocks, { onlySelected: hasProducts });
    map['{{quote.total}}'] = total.max > 0 ? formatAmountRange(total, formatCurrency) : '';
    if (hubspotDataLoaded) {
      fillMissingHubSpotSalesPlaceholders(map, { onlyIfDraft: false });
    }
    return map;
  }, [variables, localQuote, blocks, primaryContact, hubspotDataLoaded]);

  const resolveVariables = useCallback((text: string) => {
    let resolved = text
      .replace(/Start hier met je offerte…/g, '')
      .replace(/Start hier met je offerte\.\.\./g, '')
      .replace(/Start hier met je tekst…/g, '')
      .replace(/Start hier met je tekst\.\.\./g, '');

    const entries = Object.entries(variablesMap);
    entries.sort((a, b) => b[0].length - a[0].length);
    entries.forEach(([token, value]) => {
      const escapedToken = token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      resolved = resolved.replace(new RegExp(escapedToken, 'g'), value);
    });

    return resolved;
  }, [variablesMap]);

  return { variables, variablesMap, resolveVariables };
}
